import { A_LEVEL_RANK } from './filterMedSchools';

const UCAT_MODES = new Set([
  'simple_threshold',
  'composite',
  'no_fixed_threshold',
  'not_published',
  'not_used',
  'other_test',
]);

const CONFIDENCE = new Set(['high', 'medium', 'low', 'insufficient']);

const SOURCE_GROUP_ORDER = [
  'official',
  'ucat',
  'msc',
  'gmc',
  'ucas',
  'government',
  'other',
];

function isDev() {
  return process.env.NODE_ENV !== 'production';
}

function warn(path, message) {
  if (isDev()) {
    console.warn(`[med-schools] ${path}: ${message}`);
  }
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function firstDefined(obj, keys) {
  if (!isPlainObject(obj)) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') return obj[key];
  }
  return undefined;
}

function asString(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

/** Research-pass meta / "could not verify" copy — omit rather than show. */
const RESEARCH_PLACEHOLDER_RE = new RegExp([
  'not (sufficiently |fully )?verified',
  'could not be verified',
  'not systematically',
  'school-by-school official verification',
  'were not included unless',
  'this (data )?pass',
  'supplied guide',
  'fields left null',
  'sufficiently current',
  'authoritative and comparable',
  'complete cycle-specific',
  'complete 2027 contextual policy',
  'rules were not fully verified',
  'no reliable cycle-specific',
  'no current reliable (filter )?threshold was verified',
  'not used by the live filter',
  'main guide source covers',
  'does not systematically cover',
  'during this pass',
  'explicitly presented as historical',
  'not as instructions',
].join('|'), 'i');

function isResearchPlaceholder(text) {
  const value = asString(text);
  if (!value) return false;
  return RESEARCH_PLACEHOLDER_RE.test(value);
}

/** Drop research-meta clauses; keep real facts (e.g. "MMI" from "MMI; …not verified"). */
function scrubProse(text) {
  const raw = asString(text);
  if (!raw) return null;
  if (!isResearchPlaceholder(raw) && !/[;.]/.test(raw)) return raw;

  const clauses = raw.includes(';')
    ? raw.split(/\s*;\s*/)
    : raw.split(/(?<=\.)\s+/);

  if (clauses.length <= 1) {
    return isResearchPlaceholder(raw) ? null : raw;
  }

  const kept = clauses
    .map((clause) => clause.trim())
    .filter(Boolean)
    .filter((clause) => !isResearchPlaceholder(clause));
  if (!kept.length) return null;
  return kept.join(raw.includes(';') ? '; ' : ' ').replace(/\s+/g, ' ').trim() || null;
}

function scrubStringArray(values) {
  return asStringArray(values).map(scrubProse).filter(Boolean);
}

function asBoolean(value) {
  if (typeof value === 'boolean') return value;
  return null;
}

function asNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function asStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => asString(item)).filter(Boolean);
}

function isSafeHttpUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function isUrlSafeId(id) {
  return typeof id === 'string' && /^[a-z0-9][a-z0-9-]{0,120}$/.test(id);
}

function pickLocation(raw) {
  const loc = isPlainObject(raw.location) ? raw.location : raw;
  const city = asString(firstDefined(loc, ['city', 'town']));
  const region = asString(firstDefined(loc, ['region']));
  if (city && region) return { city, region, locationLabel: `${city} · ${region}` };
  const labelFromRaw = asString(firstDefined(loc, ['display_location', 'label', 'location_label', 'display']));
  if (labelFromRaw) return { city, region, locationLabel: labelFromRaw };
  if (city) return { city, region: region || null, locationLabel: city };
  if (region) return { city: null, region, locationLabel: region };
  return { city: null, region: null, locationLabel: null };
}

function pickCourse(raw) {
  const course = isPlainObject(raw.primary_course) ? raw.primary_course : (isPlainObject(raw.primaryCourse) ? raw.primaryCourse : raw);
  return {
    courseName: asString(firstDefined(course, ['name', 'course_name', 'courseName', 'title'])),
    courseCode: asString(firstDefined(course, ['code', 'course_code', 'courseCode', 'ucas_code', 'ucasCode'])),
    summary: asString(firstDefined(course, ['summary', 'overview', 'description'])),
  };
}

function normalizeMode(value) {
  const mode = asString(value);
  if (mode && UCAT_MODES.has(mode)) return mode;
  return null;
}

function ucatManualNote(mode, otherLabel) {
  switch (mode) {
    case 'composite':
      return 'UCAT forms part of a composite score';
    case 'no_fixed_threshold':
      return 'No fixed threshold published';
    case 'not_published':
      return 'Threshold not published';
    case 'not_used':
      return 'UCAT not used';
    case 'other_test':
      return otherLabel || 'Another admissions test is used';
    default:
      return null;
  }
}

function sourceGroup(source) {
  if (source.official) return 'official';
  const blob = `${source.title || ''} ${source.publisher || ''} ${source.url || ''}`.toLowerCase();
  if (blob.includes('ucat.ac.uk') || blob.includes('university clinical aptitude')) return 'ucat';
  if (blob.includes('medschools.ac.uk') || blob.includes('medical schools council')) return 'msc';
  if (blob.includes('gmc-uk.org') || /\bgmc\b/.test(blob)) return 'gmc';
  if (blob.includes('ucas.com') || /\bucas\b/.test(blob)) return 'ucas';
  if (blob.includes('gov.uk') || blob.includes('nhs.uk') || blob.includes('nhs ')) return 'government';
  return 'other';
}

function sortSources(sources) {
  return [...sources].sort((a, b) => {
    const ga = SOURCE_GROUP_ORDER.indexOf(sourceGroup(a));
    const gb = SOURCE_GROUP_ORDER.indexOf(sourceGroup(b));
    if (ga !== gb) return ga - gb;
    return String(a.title).localeCompare(String(b.title), 'en-GB');
  });
}

function normalizeSources(rawSources, schoolId, dataWarnings) {
  const list = Array.isArray(rawSources) ? rawSources : [];
  const byId = new Map();
  list.forEach((item, index) => {
    if (!isPlainObject(item)) return;
    const id = asString(item.id) || `src-${index + 1}`;
    const url = asString(item.url || item.href);
    const path = `${schoolId}.sources.${id}`;
    if (!url || !isSafeHttpUrl(url)) {
      warn(path, 'invalid or non-http(s) url');
      dataWarnings.push(`Invalid source URL for ${id}`);
      return;
    }
    const sourceType = asString(item.source_type || item.sourceType);
    const official = asBoolean(item.official) === true || sourceType === 'official_course_page';
    byId.set(id, {
      id,
      title: asString(item.title || item.label) || 'Source',
      publisher: asString(item.publisher),
      url,
      accessedAt: asString(item.accessed_date || item.accessed_at || item.accessedAt),
      cycle: asString(item.entry_cycle || item.cycle),
      official: official || null,
    });
  });
  return byId;
}

function resolveSourceIds(rawIds, sourceMap, schoolId, fieldPath, dataWarnings) {
  const ids = asStringArray(rawIds);
  const resolved = [];
  ids.forEach((id) => {
    if (sourceMap.has(id)) {
      resolved.push(id);
    } else {
      warn(`${schoolId}.${fieldPath}`, `missing source ${id}`);
      dataWarnings.push(`Missing source ${id} at ${fieldPath}`);
    }
  });
  return resolved;
}

function asFact(raw, fallbackLabel, sourceMap, schoolId, fieldPath, dataWarnings) {
  if (raw == null) {
    return {
      label: fallbackLabel,
      value: null,
      note: null,
      sourceIds: [],
    };
  }
  if (typeof raw === 'string' || typeof raw === 'number') {
    return {
      label: fallbackLabel,
      value: scrubProse(raw),
      note: null,
      sourceIds: [],
    };
  }
  if (!isPlainObject(raw)) {
    return { label: fallbackLabel, value: null, note: null, sourceIds: [] };
  }
  return {
    label: asString(raw.label) || fallbackLabel,
    value: scrubProse(firstDefined(raw, ['value', 'text', 'offer', 'grades', 'summary'])),
    note: scrubProse(firstDefined(raw, ['note', 'detail', 'subjects', 'contextual'])),
    sourceIds: resolveSourceIds(raw.source_ids || raw.sourceIds, sourceMap, schoolId, fieldPath, dataWarnings),
  };
}

function asSection(raw, sourceMap, schoolId, fieldPath, dataWarnings) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const summary = scrubProse(raw);
    return summary ? { summary, bullets: [], sourceIds: [] } : null;
  }
  if (!isPlainObject(raw) && !Array.isArray(raw)) return null;
  if (Array.isArray(raw)) {
    const bullets = scrubStringArray(raw);
    if (!bullets.length) return null;
    return { summary: null, bullets, sourceIds: [] };
  }
  const bullets = scrubStringArray(raw.bullets || raw.points);
  const summary = scrubProse(firstDefined(raw, ['summary', 'text', 'prose', 'detail']));
  const sourceIds = resolveSourceIds(raw.source_ids || raw.sourceIds, sourceMap, schoolId, fieldPath, dataWarnings);
  if (!summary && !bullets.length) return null;
  return { summary, bullets, sourceIds };
}

function asLinks(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((item) => {
      if (typeof item === 'string' && isSafeHttpUrl(item)) {
        return { href: item, label: item };
      }
      if (!isPlainObject(item)) return null;
      const href = asString(item.href || item.url);
      if (!href || !isSafeHttpUrl(href)) return null;
      return {
        href,
        label: asString(item.label || item.title) || href,
        sourceIds: asStringArray(item.source_ids || item.sourceIds),
      };
    })
    .filter(Boolean);
}

function labelledValue(label, value) {
  if (value === 'unknown' || value == null || value === '') return null;
  if (typeof value === 'boolean') return `${label}: ${value ? 'Yes' : 'No'}`;
  if (typeof value === 'number' && Number.isFinite(value)) return `${label}: ${value}`;
  if (typeof value === 'string' && value.trim()) return `${label}: ${value.trim()}`;
  if (Array.isArray(value) && value.length) {
    const parts = value.map((item) => asString(item)).filter(Boolean);
    if (parts.length) return `${label}: ${parts.join(', ')}`;
  }
  return null;
}

function sectionFromRecord(raw, sourceMap, schoolId, fieldPath, dataWarnings, fieldLabels = []) {
  if (!isPlainObject(raw)) return asSection(raw, sourceMap, schoolId, fieldPath, dataWarnings);
  const summary = scrubProse(raw.display_summary || raw.summary || raw.text || raw.prose);
  const bullets = [
    ...scrubStringArray(raw.bullets || raw.points),
    ...fieldLabels
      .map(([key, label]) => labelledValue(label, raw[key]))
      .map(scrubProse)
      .filter(Boolean),
  ];
  const sourceIds = resolveSourceIds(raw.source_ids || raw.sourceIds, sourceMap, schoolId, fieldPath, dataWarnings);
  if (!summary && !bullets.length) return null;
  return { summary, bullets, sourceIds };
}

function interviewSection(raw, sourceMap, schoolId, dataWarnings) {
  const base = sectionFromRecord(raw, sourceMap, schoolId, 'interview', dataWarnings, [
    ['format', 'Format'],
    ['delivery', 'Delivery'],
    ['duration_minutes', 'Duration (minutes)'],
    ['station_count', 'Stations'],
    ['final_offer_method', 'How offers are made'],
    ['topics_assessed', 'Assessed qualities'],
  ]);
  if (!isPlainObject(raw)) return base;
  const stats = Array.isArray(raw.historical_statistics) ? raw.historical_statistics : [];
  const extra = [];
  stats.forEach((row) => {
    if (!isPlainObject(row)) return;
    const bits = [];
    if (row.entry_year) bits.push(`${row.entry_year} entry`);
    if (asString(row.applicant_group)) bits.push(asString(row.applicant_group));
    const nums = [
      labelledValue('applicants', row.applicants),
      labelledValue('interviews', row.interviews),
      labelledValue('offers', row.offers),
      labelledValue('places', row.places),
      labelledValue('applicants per interview', row.applicants_per_interview),
      labelledValue('applicants per place', row.applicants_per_place),
      labelledValue('post-interview offer rate %', row.post_interview_offer_rate_percent),
    ].filter(Boolean);
    if (!nums.length) return;
    extra.push([bits.join(', '), nums.join('; ')].filter(Boolean).join(' — '));
    resolveSourceIds(row.source_ids, sourceMap, schoolId, 'interview.historical_statistics', dataWarnings);
  });
  if (!base && !extra.length) return null;
  const sourceIds = [...(base?.sourceIds || [])];
  extra.forEach(() => {});
  return {
    summary: scrubProse(base?.summary) || null,
    bullets: [...(base?.bullets || []), ...extra].map(scrubProse).filter(Boolean),
    sourceIds,
  };
}

function asSteps(raw, sourceMap, schoolId, dataWarnings) {
  const process = Array.isArray(raw) ? { steps: raw } : (isPlainObject(raw) ? raw : { steps: raw });
  const stepsRaw = process.steps || process.items;
  if (Array.isArray(stepsRaw) && stepsRaw.length) {
    const steps = stepsRaw
      .map((item, index) => {
        if (typeof item === 'string') {
          return { title: item, detail: null, sourceIds: [] };
        }
        if (!isPlainObject(item)) return null;
        const title = asString(item.title || item.label || item.name);
        if (!title) return null;
        return {
          title,
          detail: scrubProse(item.description || item.detail || item.text || item.summary),
          sourceIds: resolveSourceIds(
            item.source_ids || item.sourceIds,
            sourceMap,
            schoolId,
            `selection_process.steps.${index}`,
            dataWarnings
          ),
        };
      })
      .filter(Boolean);
    return { steps, prose: scrubProse(process.prose || process.text) };
  }
  const prose = scrubProse(firstDefined(process, ['prose', 'text', 'summary']) || (typeof raw === 'string' ? raw : null));
  return { steps: [], prose };
}

function normalizeStars(value, schoolId, dataWarnings) {
  if (value == null || value === '') return null;
  const n = asNumber(value);
  if (n === 1 || n === 2 || n === 3 || n === 4 || n === 5) return n;
  warn(`${schoolId}.difficulty.stars`, 'expected 1–5 or null');
  dataWarnings.push('Invalid difficulty stars');
  return null;
}

function normalizeALevelRank(value, schoolId, dataWarnings) {
  if (value == null || value === '') return null;
  const n = asNumber(value);
  if (n === 1 || n === 2 || n === 3 || n === 4 || n === 5 || n === 99) return n;
  warn(`${schoolId}.filter_data.a_level_rank`, 'expected 1–5, 99, or null');
  dataWarnings.push('Invalid A-level rank');
  return null;
}

function normalizeSchool(raw, index) {
  const dataWarnings = [];
  const id = asString(raw?.id) || `school-${index + 1}`;
  if (!isUrlSafeId(id)) {
    warn(id, 'id is not URL-safe');
    dataWarnings.push('School id is not URL-safe');
  }
  const name = asString(firstDefined(raw, ['display_name', 'name', 'medical_school_name', 'official_name', 'officialName']));
  if (!name) {
    warn(id, 'missing name');
    dataWarnings.push('Missing school name');
  }
  const { city, region, locationLabel } = pickLocation(raw);
  if (!locationLabel) {
    warn(id, 'missing location');
    dataWarnings.push('Missing location');
  }

  const sourceMap = normalizeSources(raw.sources, id, dataWarnings);
  const sources = sortSources([...sourceMap.values()]);

  const filterData = isPlainObject(raw.filter_data) ? raw.filter_data : (isPlainObject(raw.filterData) ? raw.filterData : {});
  const ucatFilter = isPlainObject(filterData.ucat) ? filterData.ucat : filterData;
  const aLevelFilter = isPlainObject(filterData.a_level) ? filterData.a_level : filterData;
  const admissions = isPlainObject(raw.admissions_test) ? raw.admissions_test : (isPlainObject(raw.admissionsTest) ? raw.admissionsTest : {});
  const ucatMode = normalizeMode(
    firstDefined(ucatFilter, ['mode', 'ucat_mode', 'ucatMode'])
    || firstDefined(admissions, ['mode', 'ucat_mode', 'ucatMode'])
  ) || 'not_published';

  let ucatThreshold = asNumber(firstDefined(ucatFilter, ['threshold', 'ucat_threshold', 'ucatThreshold']));
  if (ucatMode === 'simple_threshold') {
    if (ucatThreshold == null || ucatThreshold < 900 || ucatThreshold > 2700) {
      warn(`${id}.filter_data.ucat.threshold`, 'simple_threshold requires 900–2700');
      dataWarnings.push('Missing or invalid UCAT threshold');
      ucatThreshold = null;
    }
  } else {
    ucatThreshold = null;
  }

  const aLevelRank = normalizeALevelRank(
    firstDefined(aLevelFilter, ['rank', 'a_level_rank', 'aLevelRank']),
    id,
    dataWarnings
  );

  const difficultyRaw = isPlainObject(raw.difficulty) ? raw.difficulty : {};
  const confidenceRaw = asString(difficultyRaw.confidence);
  const confidence = confidenceRaw && CONFIDENCE.has(confidenceRaw) ? confidenceRaw : null;
  const stars = confidence === 'insufficient' ? null : normalizeStars(difficultyRaw.stars, id, dataWarnings);

  const requirementsRaw = isPlainObject(raw.requirements) ? raw.requirements : {};
  const course = pickCourse(raw);
  const summary = scrubProse(firstDefined(raw, ['short_summary', 'summary', 'overview']) || course.summary);

  const selection = asSteps(raw.selection_process || raw.selectionProcess, sourceMap, id, dataWarnings);

  const competitiveRaw = isPlainObject(raw.competitive_profile)
    ? raw.competitive_profile
    : (isPlainObject(raw.competitiveProfile) ? raw.competitiveProfile : null);

  const officialFlag = asBoolean(raw.quality?.official_sources_prioritised)
    ?? asBoolean(raw.quality?.officialSourcesPrioritised);
  // Keep the flag for data consumers, but the panel no longer surfaces a badge.
  const officialSourcesPrioritised = officialFlag === true || sources.some((s) => s.official === true);

  const otherTest = isPlainObject(admissions.other_test) ? admissions.other_test : {};
  const otherTestLabel = asString(
    firstDefined(otherTest, ['name', 'details'])
    || firstDefined(ucatFilter, ['display_text', 'other_test_label'])
  );

  const aLevelsRaw = requirementsRaw.a_levels || requirementsRaw.a_level || requirementsRaw.aLevel;
  const gcseRaw = requirementsRaw.gcses || requirementsRaw.gcse;
  const sjtRaw = (isPlainObject(admissions.sjt) ? admissions.sjt : null) || requirementsRaw.sjt;
  const altRaw = requirementsRaw.alternative_qualifications || requirementsRaw.alternatives || [];
  const deferredRaw = requirementsRaw.reapplication_and_deferred_entry || requirementsRaw.deferred_entry || requirementsRaw.deferredEntry;
  const resitNote = isPlainObject(aLevelsRaw) ? asString(aLevelsRaw.resit_policy) : null;

  const aLevelFact = isPlainObject(aLevelsRaw)
    ? {
      label: 'A levels',
      value: scrubProse(
        asString(aLevelsRaw.standard_offer) || asString(aLevelsRaw.display_summary) || asString(aLevelsRaw.value)
      ),
      note: scrubProse([
        asString(aLevelsRaw.display_summary) && asString(aLevelsRaw.display_summary) !== asString(aLevelsRaw.standard_offer)
          ? asString(aLevelsRaw.display_summary)
          : asStringArray(aLevelsRaw.required_subjects).length
            ? `Required subjects: ${asStringArray(aLevelsRaw.required_subjects).join('; ')}`
            : null,
        asString(aLevelsRaw.contextual_offer)
          ? `Contextual offer (not the standard requirement): ${asString(aLevelsRaw.contextual_offer)}`
          : null,
      ].filter(Boolean).join(' ') || null),
      sourceIds: resolveSourceIds(aLevelsRaw.source_ids || aLevelsRaw.sourceIds, sourceMap, id, 'requirements.a_levels', dataWarnings),
    }
    : asFact(aLevelsRaw, 'A levels', sourceMap, id, 'requirements.a_level', dataWarnings);

  const gcseFact = isPlainObject(gcseRaw)
    ? {
      label: 'GCSEs',
      value: scrubProse(
        asString(gcseRaw.display_summary)
        || asString(gcseRaw.scoring_method)
        || asString(gcseRaw.minimum_overall_rule)
        || (gcseRaw.are_gcses_scored === true ? 'GCSEs are scored' : null)
      ),
      note: scrubProse([
        asString(gcseRaw.english_requirement) ? `English: ${gcseRaw.english_requirement}` : null,
        asString(gcseRaw.maths_requirement) ? `Maths: ${gcseRaw.maths_requirement}` : null,
        asString(gcseRaw.science_requirement) ? `Science: ${gcseRaw.science_requirement}` : null,
      ].filter(Boolean).join(' ') || null),
      sourceIds: resolveSourceIds(gcseRaw.source_ids, sourceMap, id, 'requirements.gcses', dataWarnings),
    }
    : asFact(gcseRaw, 'GCSEs', sourceMap, id, 'requirements.gcse', dataWarnings);

  const admissionsFact = {
    label: 'Admissions test',
    value: scrubProse(
      asString(ucatFilter.display_text)
      || (otherTest.used ? asString(otherTest.name) : null)
      || asString(isPlainObject(admissions.ucat) ? admissions.ucat.usage_summary : null)
    ),
    note: scrubProse(
      asString(ucatFilter.filter_note) || asString(isPlainObject(admissions.ucat) ? admissions.ucat.usage_summary : null)
    ),
    sourceIds: resolveSourceIds(
      ucatFilter.source_ids || (isPlainObject(admissions.ucat) ? admissions.ucat.source_ids : null),
      sourceMap,
      id,
      'admissions_test',
      dataWarnings
    ),
  };

  const sjtFact = isPlainObject(sjtRaw)
    ? {
      label: 'SJT',
      value: scrubProse(
        asString(sjtRaw.usage_summary)
        || (asStringArray(sjtRaw.automatically_rejected_bands).length
          ? `Band ${asStringArray(sjtRaw.automatically_rejected_bands).join(', ')} typically excluded`
          : null)
        || (Array.isArray(sjtRaw.accepted_bands) && sjtRaw.accepted_bands.length
          ? `Accepted bands: ${sjtRaw.accepted_bands.join(', ')}`
          : null)
      ),
      note: null,
      sourceIds: resolveSourceIds(sjtRaw.source_ids, sourceMap, id, 'admissions_test.sjt', dataWarnings),
    }
    : asFact(sjtRaw, 'SJT', sourceMap, id, 'requirements.sjt', dataWarnings);

  const competitiveCaveats = scrubStringArray(competitiveRaw?.important_caveats);
  const competitiveIndicators = Array.isArray(competitiveRaw?.indicators)
    ? competitiveRaw.indicators
      .map((item) => {
        if (typeof item === 'string') return scrubProse(item);
        if (!isPlainObject(item) || item.value == null) return null;
        const cycle = item.cycle ? ` (${item.cycle})` : '';
        return asString(item.label) ? scrubProse(`${item.label}: ${item.value}${cycle}`) : null;
      })
      .filter(Boolean)
    : [];

  const school = {
    id,
    name: name || 'Medical school',
    universityName: asString(firstDefined(raw, ['university_name', 'universityName'])) || name || null,
    courseName: course.courseName,
    courseCode: course.courseCode,
    locationLabel: locationLabel || 'Location not published',
    city,
    region,
    summary,
    difficulty: {
      stars,
      confidence,
      explanation: asString(difficultyRaw.explanation || difficultyRaw.calculation_note),
    },
    filters: {
      ucatMode,
      ucatThreshold,
      ucatThresholdLabel: asString(firstDefined(ucatFilter, ['display_text', 'ucat_threshold_label', 'ucatThresholdLabel'])),
      aLevelRank,
      aLevelLabel: asString(firstDefined(aLevelFilter, ['standard_offer_display', 'a_level_label', 'aLevelLabel'])),
    },
    requirements: {
      aLevel: aLevelFact,
      gcse: gcseFact,
      admissionsTest: admissionsFact,
      sjt: sjtFact,
      alternatives: (Array.isArray(altRaw) ? altRaw : [])
        .map((item, i) => asFact(item, 'Alternative qualification', sourceMap, id, `requirements.alternatives.${i}`, dataWarnings))
        .filter((fact) => fact.value || fact.note),
      englishLanguage: requirementsRaw.english_language || requirementsRaw.englishLanguage
        ? asFact(
          isPlainObject(requirementsRaw.english_language)
            ? { value: requirementsRaw.english_language.requirement, source_ids: requirementsRaw.english_language.source_ids }
            : requirementsRaw.english_language || requirementsRaw.englishLanguage,
          'English language',
          sourceMap,
          id,
          'requirements.english_language',
          dataWarnings
        )
        : null,
      graduateApplicants: requirementsRaw.graduate_applicants || requirementsRaw.graduateApplicants
        ? asFact(
          isPlainObject(requirementsRaw.graduate_applicants)
            ? { value: requirementsRaw.graduate_applicants.details, note: requirementsRaw.graduate_applicants.degree_classification, source_ids: requirementsRaw.graduate_applicants.source_ids }
            : requirementsRaw.graduate_applicants,
          'Graduate applicants',
          sourceMap,
          id,
          'requirements.graduate_applicants',
          dataWarnings
        )
        : null,
      resits: resitNote
        ? { label: 'Resits', value: scrubProse(resitNote), note: null, sourceIds: resolveSourceIds(aLevelsRaw.source_ids, sourceMap, id, 'requirements.a_levels.resit', dataWarnings) }
        : null,
      deferredEntry: deferredRaw
        ? asFact(
          isPlainObject(deferredRaw)
            ? { value: deferredRaw.deferred_entry_policy || deferredRaw.reapplication_policy, note: deferredRaw.previous_interviewee_policy, source_ids: deferredRaw.source_ids }
            : deferredRaw,
          'Deferred entry',
          sourceMap,
          id,
          'requirements.deferred_entry',
          dataWarnings
        )
        : null,
    },
    competitiveProfile: {
      text: scrubProse(competitiveRaw?.summary || competitiveRaw?.text)
        || (competitiveIndicators.length ? competitiveIndicators.join('. ') : null),
      evidenceCycle: asString(competitiveRaw?.evidence_cycle || competitiveRaw?.evidenceCycle || competitiveRaw?.cycle),
      caveat: scrubProse(competitiveRaw?.caveat) || (competitiveCaveats.length ? competitiveCaveats.join(' ') : null),
      sourceIds: competitiveRaw
        ? resolveSourceIds(competitiveRaw.source_ids || competitiveRaw.sourceIds, sourceMap, id, 'competitive_profile', dataWarnings)
        : [],
    },
    selectionSteps: selection.steps,
    selectionProse: selection.prose,
    interview: interviewSection(raw.interview, sourceMap, id, dataWarnings),
    contextual: sectionFromRecord(
      raw.contextual_and_widening_participation || raw.contextual,
      sourceMap,
      id,
      'contextual',
      dataWarnings,
      [
        ['how_to_check_eligibility', 'How to check eligibility'],
        ['standard_contextual_offer', 'Contextual offer'],
        ['ucat_adjustment', 'UCAT adjustment'],
        ['named_programmes', 'Programmes'],
        ['guaranteed_interview_conditions', 'Interview conditions'],
      ]
    ),
    courseAndTeaching: sectionFromRecord(
      raw.course_and_teaching || raw.courseAndTeaching,
      sourceMap,
      id,
      'course_and_teaching',
      dataWarnings,
      [
        ['course_structure_summary', 'Structure'],
        ['teaching_model', 'Teaching'],
        ['early_clinical_contact', 'Early clinical contact'],
        ['anatomy_teaching', 'Anatomy'],
      ]
    ),
    placements: sectionFromRecord(raw.placements, sourceMap, id, 'placements', dataWarnings, [
      ['travel_expectation', 'Travel'],
      ['placement_regions', 'Placement regions'],
      ['primary_care_exposure', 'Primary care'],
    ]),
    studentExperience: sectionFromRecord(
      raw.student_experience || raw.studentExperience,
      sourceMap,
      id,
      'student_experience',
      dataWarnings,
      [
        ['university_environment', 'Environment'],
        ['accommodation_summary', 'Accommodation'],
        ['medical_cohort_size', 'Cohort size'],
      ]
    ),
    geographyAndCity: sectionFromRecord(
      raw.geography_and_city || raw.geographyAndCity,
      sourceMap,
      id,
      'geography_and_city',
      dataWarnings,
      [
        ['city_character', 'City'],
        ['placement_geography', 'Placement geography'],
        ['public_transport', 'Transport'],
      ]
    ),
    money: sectionFromRecord(raw.money, sourceMap, id, 'money', dataWarnings, [
      ['fee_year', 'Fee year'],
      ['home_tuition_fee_gbp_per_year', 'Home tuition fee (£ / year)'],
      ['international_tuition_fee_gbp_per_year', 'International tuition fee (£ / year)'],
      ['placement_travel_cost_note', 'Placement travel'],
      ['nhs_funding_note', 'NHS funding'],
    ]),
    usefulLinks: asLinks(
      Array.isArray(raw.useful_links)
        ? raw.useful_links
        : Object.entries(raw.useful_links || {}).map(([key, href]) => ({ href, label: key.replace(/_/g, ' ') }))
    ),
    sources,
    lastVerified: asString(
      raw.quality?.last_verified_at
      || raw.quality?.last_verified
      || raw.quality?.lastVerified
      || raw.last_verified
      || raw.lastVerified
    ),
    dataWarnings,
    checkQualificationDetails: aLevelRank == null || aLevelRank === A_LEVEL_RANK.OTHER_OR_NON_COMPARABLE,
    ucatManualNote: ucatManualNote(ucatMode, otherTestLabel),
    officialSourcesPrioritised,
  };

  ['englishLanguage', 'graduateApplicants', 'resits', 'deferredEntry'].forEach((key) => {
    const fact = school.requirements[key];
    if (fact && !fact.value && !fact.note) school.requirements[key] = null;
  });

  return school;
}

export function normalizeMedSchoolData(raw) {
  const datasetRaw = isPlainObject(raw?.dataset) ? raw.dataset : {};
  const schoolsRaw = Array.isArray(raw?.schools) ? raw.schools : [];
  const ids = new Set();
  const schools = schoolsRaw.map((item, index) => {
    const school = normalizeSchool(item, index);
    if (ids.has(school.id)) {
      warn(school.id, 'duplicate id');
      school.dataWarnings.push('Duplicate school id');
    }
    ids.add(school.id);
    return school;
  });

  const coverage = isPlainObject(raw?.coverage_audit) ? raw.coverage_audit : (isPlainObject(raw?.coverageAudit) ? raw.coverageAudit : {});
  const intendedCount = asNumber(
    coverage.intended_count
    || coverage.intendedCount
    || (Array.isArray(coverage.included_school_ids) ? coverage.included_school_ids.length : null)
    || coverage.medical_schools_council_school_count
  );
  if (intendedCount != null && intendedCount !== schools.length) {
    warn('coverage_audit', `intended_count ${intendedCount} !== schools ${schools.length}`);
  }

  const entryYear = asNumber(datasetRaw.entry_year || datasetRaw.entryYear);
  const entryCycle = asString(datasetRaw.entry_cycle || datasetRaw.entryCycle)
    || (entryYear != null ? `${entryYear} entry` : null);

  return {
    schemaVersion: asString(raw?.schema_version || raw?.schemaVersion),
    dataset: {
      entryCycle,
      name: asString(datasetRaw.title || datasetRaw.name),
    },
    filterConfig: raw?.filter_config || raw?.filterConfig || null,
    difficultyMethodology: asString(
      isPlainObject(raw?.difficulty_methodology)
        ? raw.difficulty_methodology.summary
        : raw?.difficulty_methodology || raw?.difficultyMethodology
    ),
    coverageAudit: coverage,
    schools,
  };
}

export { isSafeHttpUrl, isUrlSafeId, scrubProse, isResearchPlaceholder };
